import { Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { Block } from '@core/models/block/block.model';
import { SubSink } from '@core/utils/subsink';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { SnackBarMessageComponent } from '@shared/components/snack-bar-message.component';
import { FormService } from '@modules/creator/components/special-block/forms/services/form.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContactFormService } from '@modules/creator/components/special-block/forms/contact-form/contact-form.service';
import { BlockAction } from '@core/models';
import { BlockActionType } from '@core/enums';
import { ClientService } from '@core/services/client/client.service';
import { ApiReCaptchaService } from '@core/services/common/api-re-captcha.service';
import { PageService } from '@core/services/page/page.service';
import { ShoppingCartService } from '@shared/services/cart/shopping-cart.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';

@Component({
    selector: 'app-contact-form',
    encapsulation: ViewEncapsulation.None,
    template: `
    <form [formGroup]="formGroup">
      <div app-block-container [children]="specialBlock.children"></div>
    </form>
  `,
    providers: [FormService, ContactFormService],
    standalone: false
})
export class ContactFormComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;
  formGroup = this._fs.formGroup;
  private subSink = new SubSink();
  private isSendingInProgress = false;

  constructor(private element: ElementRef,
              private _snackBar: MatSnackBar,
              private _fs: FormService,
              private clientService: ClientService,
              private apiReCaptchaService: ApiReCaptchaService,
              private contactFormService: ContactFormService,
              private _buttonHandlerService: ButtonHandlerService,
              private shoppingCart: ShoppingCartService,
              private pageService: PageService,
              private currencyPreference: CurrencyPreferenceService) {
  }

  ngOnInit(): void {
    this.subSink.sink =
      this._buttonHandlerService.click$
        .pipe(
          filter((action: BlockAction) => action.actionType === BlockActionType.SUBMIT),
          tap(_ => this.formGroup.markAllAsTouched()),
          filter(() => this.formGroup.valid),
        )
        .subscribe(
          async (action: BlockAction) => {
            const path = action.actionData.path;
            const reCaptchaResult = await this.apiReCaptchaService.verifyRecaptchaIfNeeded();
            // if robot then return and do nothing
            if (reCaptchaResult === false) {
              return;
            }

            if (this.isSendingInProgress) {
              return;
            }
            this.isSendingInProgress = true;
            const sendMsgRes = await this.sendMessage(path);
            if (sendMsgRes) {
              // jesli block jest w pop-up'ie to po poprawnym wyslaniu wiadomosci zamykamy pop-up
              if (this.element.nativeElement.closest('app-popup')) {
                this._buttonHandlerService.click$.next({
                  ...action,
                  actionType: BlockActionType.CLOSE
                });
              }
            }
            this._snackBar
              .openFromComponent(SnackBarMessageComponent, {
                data: sendMsgRes ? 'contactForm.success' : 'contactForm.error',
              })
              .afterDismissed()
              .subscribe(_ => {
                this.isSendingInProgress = false;
              });
          },
        );

    this.subSink.sink =
      this._buttonHandlerService.click$
        .pipe(
          filter((action: BlockAction) => action.actionType === BlockActionType.CANCEL),
          tap(_ => this.formGroup.reset())
        )
        .subscribe();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  /**
   * Ładunek zapytania wraz z wyborem z konfiguratora ceny.
   *
   * Metoda jest publiczna, bo to jej wynik rozstrzyga, co leci do backendu.
   *
   * Wybór bierzemy z serwisu koszyka, bo to tam publikuje go konfigurator — ten sam nośnik
   * obsługuje przycisk „Kup", który również jest blokiem strony i konfiguratora nie zna.
   * Nośnik trzyma wybór razem z pracą, której dotyczy, więc wybór z poprzednio oglądanej
   * karty nie wycieknie do zapytania o bieżącą.
   *
   * Brak wyboru zostawia ładunek bez `variant_id` — backend wyceni wtedy wariant domyślny.
   */
  ladunek(): Record<string, unknown> {
    const dane = this.formGroup.getRawValue();
    const pracaId = this.pageService.page.value?.modelId ?? null;
    const wariant = this.shoppingCart.wariantDlaPracy(pracaId);

    if (wariant !== null) {
      dane.variant_id = wariant;
    }

    dane.currency = this.currencyPreference.getCurrentCurrency();

    return dane;
  }

  private async sendMessage(path: string): Promise<boolean> {
    this.formGroup.setErrors({'status': true});
    return new Promise((resolve, reject) => {
      this.contactFormService.sendMessage(path, this.ladunek(), true, this.specialBlock.id)
        .pipe(
          catchError(({ok, error}) => {
            this._fs.setFormErrors(error.errors);
            return of(ok);
          })
        ).subscribe(res => {
        this.formGroup.reset();
        if (!res) {
          resolve(false);
        } else {
          resolve(true);
        }
      });
    });
  }
}
